/**
 * frontend/src/pages/KnowledgePage.jsx
 *
 * The Knowledge Base: material that teaches people how to use the TMS.
 *
 * A document is a subject, not a file. It belongs to one or more modules,
 * may carry the steps a reader should follow, and holds any number of files:
 * the guide, the video of it and the screenshots, under one title.
 *
 * Everyone reads. Only an Admin level adds, edits or removes, which the
 * server enforces as well rather than trusting this page.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, Upload, Download, Search, SquarePen, Trash2, RotateCcw,
  FileText, Eye, X, Plus, ListOrdered,
} from 'lucide-react';
import { knowledgeAPI, downloadBlob } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { isAdminRole } from '../utils/rolesConfig';
import { useMaster } from '../hooks/useMasters';
import { useToast, apiError, useConfirm } from '../components/Toast';
import { PageHeader, Modal, FormField, EmptyState } from '../components/layout/UI';

/* Modules come from Masters so an administrator can add one without a
   migration. This fallback matches what phase71 seeds, so the picker is
   never empty even if Masters is unreachable. */
const MODULE_FALLBACK = [
  'Getting Started', 'Projects', 'Tasks', 'Activities', 'Workplan',
  'Bugs and Dev Hub', 'Change Requests', 'Training',
  'Milestones and Timeline', 'Risks', 'Clients', 'Reports',
  'Administration', 'General',
];

const SAPPHIRE = '#2E539F';
const JAVA     = '#5CC3BC';
const CRIMSON  = '#C8372D';

const readableSize = (b) => {
  if (b === null || b === undefined) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const extOf = (n = '') => (n.match(/\.([a-z0-9]+)$/i)?.[1] || 'file').toUpperCase();

/** How a file should be shown, decided by its type. */
const viewKind = (mime = '', name = '') => {
  if (mime === 'application/pdf') return 'pdf';
  // SVG is excluded on purpose: it can carry script, so it is a download only.
  if (/^image\//.test(mime) && mime !== 'image/svg+xml') return 'image';
  if (/^video\//.test(mime)) return 'video';
  if (/^audio\//.test(mime)) return 'audio';
  if (/^text\//.test(mime) || mime === 'application/json') return 'text';
  if (/\.(md|txt|csv|json)$/i.test(name)) return 'text';
  return null;                       // Word, PowerPoint, Excel, zip, svg
};

const BLANK = { title:'', description:'', modules:['General'], release_tag:'', steps:'' };

export default function KnowledgePage() {
  const { user } = useAuth();
  const toast   = useToast();
  const confirm = useConfirm();
  const canPublish = isAdminRole(user?.role);
  const moduleOptions = useMaster('knowledge_module', MODULE_FALLBACK);

  const [docs,    setDocs]    = useState([]);
  const [meta,    setMeta]    = useState({ modules: [], releases: [] });
  const [limits,  setLimits]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [module,  setModule]  = useState('');
  const [release, setRelease] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [expanded, setExpanded] = useState({});     // which documents show their steps

  const [modalOpen, setModalOpen] = useState(false);
  const [editDoc,   setEditDoc]   = useState(null);
  const [form,      setForm]      = useState({ ...BLANK });
  const [files,     setFiles]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState('');

  const [viewing, setViewing] = useState(null);     // { file, url, kind, text }
  const urlRef = useRef(null);

  useEffect(() => {
    knowledgeAPI.getLimits()
      .then(r => setLimits(r.data))
      .catch(e => console.error('knowledge limits failed:', e));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)  params.search  = search;
      if (module)  params.module  = module;
      if (release) params.release = release;
      if (showRemoved) params.scope = 'deleted';
      const [d, m] = await Promise.all([knowledgeAPI.getAll(params), knowledgeAPI.getModules()]);
      setDocs(d.data || []);
      setMeta(m.data || { modules: [], releases: [] });
    } catch (e) {
      console.error('knowledge load failed:', e);           // background load
      toast.error(apiError(e, 'Could not load the Knowledge Base.'));
    } finally { setLoading(false); }
  }, [search, module, release, showRemoved, toast]);

  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  // Blob URLs are revoked when the viewer closes and when the page unmounts,
  // otherwise a long session leaks every file anybody opened.
  const closeViewer = useCallback(() => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setViewing(null);
  }, []);
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const openViewer = async (f) => {
    const kind = viewKind(f.mime_type, f.file_name);
    if (!kind) return;
    try {
      const r = await knowledgeAPI.downloadFile(f.id);
      if (kind === 'text') {
        const raw = await r.data.text();
        let body = raw;
        if (/json/i.test(f.mime_type) || /\.json$/i.test(f.file_name)) {
          try { body = JSON.stringify(JSON.parse(raw), null, 2); } catch { /* show it raw */ }
        }
        setViewing({ file: f, kind, text: body });
      } else {
        const url = URL.createObjectURL(new Blob([r.data], { type: f.mime_type }));
        urlRef.current = url;
        setViewing({ file: f, kind, url });
      }
    } catch (e) { toast.error(apiError(e, 'Could not open that file')); }
  };

  const download = async (f) => {
    try {
      const r = await knowledgeAPI.downloadFile(f.id);
      downloadBlob(new Blob([r.data]), f.file_name);
    } catch (e) { toast.error(apiError(e, 'Could not download that file')); }
  };

  const openCreate = () => {
    setEditDoc(null); setForm({ ...BLANK }); setFiles([]); setFormError(''); setModalOpen(true);
  };
  const openEdit = (d) => {
    setEditDoc(d);
    setForm({ title:d.title, description:d.description || '', modules:d.modules?.length ? d.modules : ['General'],
              release_tag:d.release_tag || '', steps:d.steps || '' });
    setFiles([]); setFormError(''); setModalOpen(true);
  };

  const toggleModule = (m) => setForm(f => ({
    ...f,
    modules: f.modules.includes(m) ? f.modules.filter(x => x !== m) : [...f.modules, m],
  }));

  const save = async () => {
    if (!form.title.trim())        return setFormError('A title is required');
    if (!form.modules.length)      return setFormError('Choose at least one module');
    if (!editDoc && !files.length) return setFormError('Choose at least one file');
    setSaving(true); setFormError('');
    try {
      if (editDoc) {
        await knowledgeAPI.update(editDoc.id, form);
        if (files.length) {
          const fd = new FormData();
          files.forEach(f => fd.append('files', f));
          await knowledgeAPI.addFiles(editDoc.id, fd);
        }
        toast.success(files.length ? 'Document updated and files added' : 'Document updated');
      } else {
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        fd.append('title', form.title);
        fd.append('description', form.description);
        fd.append('release_tag', form.release_tag);
        fd.append('steps', form.steps);
        form.modules.forEach(m => fd.append('modules', m));
        await knowledgeAPI.create(fd);
        toast.success('Document added');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      setFormError(apiError(e, 'Could not save this document'));
    } finally { setSaving(false); }
  };

  const removeFile = async (d, f) => {
    if (!await confirm(`Remove "${f.file_name}" from "${d.title}"?\n\nThis file cannot be recovered.`,
      { danger:true, title:'Remove file', confirmLabel:'Remove' })) return;
    try { await knowledgeAPI.removeFile(f.id); toast.success('File removed'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not remove that file')); }
  };

  const removeDoc = async (d) => {
    if (!await confirm(`Remove "${d.title}" from the Knowledge Base?\n\nThe files are kept and an administrator can restore it.`,
      { danger:true, title:'Remove document', confirmLabel:'Remove' })) return;
    try { await knowledgeAPI.remove(d.id); toast.success('Removed'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not remove that document')); }
  };

  const restoreDoc = async (d) => {
    try { await knowledgeAPI.restore(d.id); toast.success('Restored'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not restore that document')); }
  };

  /* Derived values, declared after every hook and read only below. */
  const filtering = !!(search || module || release);
  const extList   = limits?.extensions?.join(' ') || '';
  const maxMb     = limits ? Math.round(limits.max_file_bytes / 1024 / 1024) : null;
  const chosenBytes = files.reduce((s, f) => s + f.size, 0);
  const tooBig    = limits ? files.filter(f => f.size > limits.max_file_bytes) : [];

  return (
    <div>
      <PageHeader
        title="Knowledge Base"
        subtitle="Guides and training material for using the TMS"
        actions={canPublish && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
            <Upload size={14} /> Add Document
          </button>
        )}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-52 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}>
            <Search size={15} />
          </span>
          <input className="input pl-9 text-sm" placeholder="Search titles, descriptions and steps..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        <select className="select text-sm w-56" value={module} onChange={e => setModule(e.target.value)}>
          <option value="">All modules</option>
          {meta.modules.map(m => (
            <option key={m.module} value={m.module}>{m.module} ({m.count})</option>
          ))}
        </select>

        {meta.releases.length > 0 && (
          <select className="select text-sm w-44" value={release} onChange={e => setRelease(e.target.value)}>
            <option value="">All releases</option>
            {meta.releases.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}

        {filtering && (
          <button onClick={() => { setSearch(''); setModule(''); setRelease(''); }}
            className="text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-[var(--surface-alt)]"
            style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
            Clear filters
          </button>
        )}

        {canPublish && (
          <label className="flex items-center gap-1.5 text-xs ml-auto cursor-pointer"
            style={{ color:'var(--text-secondary)' }}>
            <input type="checkbox" checked={showRemoved}
              onChange={e => setShowRemoved(e.target.checked)} />
            Show removed
          </label>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 rounded-full animate-spin"
            style={{ borderColor:`${SAPPHIRE}33`, borderTopColor:SAPPHIRE }} />
        </div>
      ) : docs.length === 0 ? (
        <EmptyState icon={BookOpen}
          title={showRemoved ? 'Nothing has been removed'
                 : filtering ? 'No documents match those filters'
                 : 'The Knowledge Base is empty'}
          message={showRemoved ? ''
                   : filtering ? 'Try a different module or clear the filters.'
                   : canPublish ? 'Add the first guide so the team has somewhere to look.'
                   : 'An administrator has not added any material yet.'} />
      ) : (
        <div className="space-y-4">
          {docs.map(d => (
            <div key={d.id} className="card">
              {/* Title row */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold" style={{ color:'var(--text)' }}>{d.title}</h2>
                    {d.release_tag && (
                      <span className="badge text-xs" style={{ background:`${JAVA}22`, color:'#1f7a74' }}>
                        {d.release_tag}
                      </span>
                    )}
                    {d.deleted_at && (
                      <span className="badge text-xs" style={{ background:`${CRIMSON}1a`, color:CRIMSON }}>
                        Removed
                      </span>
                    )}
                  </div>
                  {d.description && (
                    <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>{d.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    {(d.modules || []).map(m => (
                      <button key={m} onClick={() => setModule(m)}
                        className="badge text-xs transition-opacity hover:opacity-80"
                        style={{ background:`${SAPPHIRE}14`, color:SAPPHIRE }}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {canPublish && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!d.deleted_at ? (
                      <>
                        <button onClick={() => openEdit(d)} title="Edit"
                          className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                          style={{ color:'var(--text-secondary)' }}><SquarePen size={15} /></button>
                        <button onClick={() => removeDoc(d)} title="Remove"
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          style={{ color:CRIMSON }}><Trash2 size={15} /></button>
                      </>
                    ) : (
                      <button onClick={() => restoreDoc(d)} title="Restore"
                        className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                        style={{ color:JAVA }}><RotateCcw size={15} /></button>
                    )}
                  </div>
                )}
              </div>

              {/* Steps */}
              {d.steps && (
                <div className="mt-3">
                  <button onClick={() => setExpanded(e => ({ ...e, [d.id]: !e[d.id] }))}
                    className="flex items-center gap-1.5 text-xs font-semibold"
                    style={{ color:SAPPHIRE }}>
                    <ListOrdered size={14} />
                    {expanded[d.id] ? 'Hide steps' : `Show steps (${d.steps.split('\n').filter(Boolean).length})`}
                  </button>
                  {expanded[d.id] && (
                    <ol className="mt-2 pl-5 space-y-1 text-sm list-decimal"
                      style={{ color:'var(--text)' }}>
                      {d.steps.split('\n').map(s => s.trim()).filter(Boolean)
                        .map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  )}
                </div>
              )}

              {/* Files */}
              {(d.files || []).length > 0 && (
                <div className="mt-3 rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
                  {d.files.map((f, i) => {
                    const kind = viewKind(f.mime_type, f.file_name);
                    return (
                      <div key={f.id}
                        className="flex items-center gap-3 px-3 py-2"
                        style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                        <FileText size={16} style={{ color:SAPPHIRE, flexShrink:0 }} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm truncate" style={{ color:'var(--text)' }}>
                            {f.file_name}
                          </span>
                          <span className="block text-xs" style={{ color:'var(--text-secondary)' }}>
                            {extOf(f.file_name)} · {readableSize(f.file_size)}
                            {!kind && ' · opens in its own application'}
                          </span>
                        </span>
                        {kind && !d.deleted_at && (
                          <button onClick={() => openViewer(f)} title="View here"
                            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                            style={{ color:SAPPHIRE }}><Eye size={15} /></button>
                        )}
                        {!d.deleted_at && (
                          <button onClick={() => download(f)} title="Download"
                            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                            style={{ color:'var(--text-secondary)' }}><Download size={15} /></button>
                        )}
                        {canPublish && !d.deleted_at && (
                          <button onClick={() => removeFile(d, f)} title="Remove this file"
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            style={{ color:CRIMSON }}><X size={15} /></button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-xs mt-2" style={{ color:'var(--text-secondary)' }}>
                {[`${(d.files || []).length} file${(d.files || []).length === 1 ? '' : 's'}`,
                  d.uploaded_by_name,
                  d.created_at && new Date(d.created_at).toLocaleDateString()]
                  .filter(Boolean).join(' · ')}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / edit ── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editDoc ? `Edit — ${editDoc.title}` : 'Add Document'} size="lg">
        <div className="space-y-3">
          {formError && (
            <div className="p-3 rounded-lg text-sm"
              style={{ background:`${CRIMSON}14`, color:CRIMSON, border:`1px solid ${CRIMSON}33` }}>
              {formError}
            </div>
          )}

          <FormField label={editDoc ? 'Add more files' : 'Files'} required={!editDoc}>
            <input type="file" multiple className="input"
              onChange={e => setFiles([...(e.target.files || [])])} />
            {limits && (
              <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
                Up to {limits.max_files} files, {maxMb} MB each. Accepted: {extList}
              </p>
            )}
            {files.length > 0 && (
              <div className="mt-2 rounded-lg overflow-hidden" style={{ border:'1px solid var(--border)' }}>
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs"
                    style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                    <span className="truncate" style={{ color:'var(--text)' }}>{f.name}</span>
                    <span style={{ color: limits && f.size > limits.max_file_bytes ? CRIMSON : 'var(--text-secondary)' }}>
                      {readableSize(f.size)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs font-semibold"
                  style={{ borderTop:'1px solid var(--border)', background:'var(--surface-alt)', color:'var(--text)' }}>
                  <span>{files.length} file{files.length === 1 ? '' : 's'}</span>
                  <span>{readableSize(chosenBytes)}</span>
                </div>
              </div>
            )}
            {tooBig.length > 0 && (
              <p className="text-xs mt-1" style={{ color:CRIMSON }}>
                {tooBig.length} file{tooBig.length === 1 ? ' is' : 's are'} over {maxMb} MB and will be refused.
              </p>
            )}
          </FormField>

          <FormField label="Title" required>
            <input className="input" value={form.title} autoFocus
              onChange={e => setForm(f => ({ ...f, title:e.target.value }))}
              placeholder="How the Workplan works" />
          </FormField>

          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description:e.target.value }))}
              placeholder="One line on what this covers" />
          </FormField>

          <FormField label="Modules" required>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-lg max-h-40 overflow-y-auto"
              style={{ border:'1px solid var(--border)', background:'var(--surface-alt)' }}>
              {moduleOptions.map(o => {
                const on = form.modules.includes(o.value);
                return (
                  <button key={o.value} type="button" onClick={() => toggleModule(o.value)}
                    className="text-xs px-2.5 py-1 rounded-full transition-colors"
                    style={on
                      ? { background:SAPPHIRE, color:'#fff', fontWeight:600 }
                      : { background:'var(--surface)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              Pick every part of the system this explains. Add new ones in Admin, Masters.
            </p>
          </FormField>

          <FormField label="Steps">
            <textarea className="input font-mono text-xs" rows={5} value={form.steps}
              onChange={e => setForm(f => ({ ...f, steps:e.target.value }))}
              placeholder={'Open Workplan\nPick the week\nDrag an activity onto a day'} />
            <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>
              One step per line. Readers see them numbered.
            </p>
          </FormField>

          <FormField label="Release">
            <input className="input" value={form.release_tag}
              onChange={e => setForm(f => ({ ...f, release_tag:e.target.value }))}
              placeholder="2026-10, optional" />
          </FormField>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin" />}
              {editDoc ? 'Save Changes' : <><Plus size={14} /> Add Document</>}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Viewer ── */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closeViewer} />
          <div className="relative rounded-2xl shadow-2xl w-full max-w-5xl z-10 flex flex-col"
            style={{ background:'var(--surface)', border:'1px solid var(--border)', maxHeight:'90vh' }}>
            <div className="px-5 py-3 border-b flex items-center justify-between gap-3"
              style={{ borderColor:'var(--border)' }}>
              <div className="min-w-0">
                <p className="font-semibold truncate" style={{ color:'var(--text)' }}>
                  {viewing.file.file_name}
                </p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                  {extOf(viewing.file.file_name)} · {readableSize(viewing.file.file_size)}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => download(viewing.file)} title="Download"
                  className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)]"
                  style={{ color:'var(--text-secondary)' }}><Download size={16} /></button>
                <button onClick={closeViewer} title="Close"
                  className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)]"
                  style={{ color:'var(--text-secondary)' }}><X size={16} /></button>
              </div>
            </div>

            <div className="p-4 overflow-auto" style={{ background:'var(--surface-alt)' }}>
              {viewing.kind === 'pdf' && (
                /* sandboxed: a PDF is content to read, not a page that needs
                   scripts or access to this origin. */
                <iframe title={viewing.file.file_name} src={viewing.url}
                  sandbox="" className="w-full rounded-lg bg-white"
                  style={{ height:'72vh', border:'none' }} />
              )}
              {viewing.kind === 'image' && (
                <img src={viewing.url} alt={viewing.file.file_name}
                  className="max-w-full mx-auto rounded-lg" style={{ maxHeight:'72vh' }} />
              )}
              {viewing.kind === 'video' && (
                <video src={viewing.url} controls className="w-full rounded-lg"
                  style={{ maxHeight:'72vh' }} />
              )}
              {viewing.kind === 'audio' && (
                <audio src={viewing.url} controls className="w-full" />
              )}
              {viewing.kind === 'text' && (
                <pre className="text-xs whitespace-pre-wrap p-3 rounded-lg overflow-auto"
                  style={{ background:'var(--surface)', color:'var(--text)', maxHeight:'72vh' }}>
                  {viewing.text}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
